import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useWizard, WizardStepLayout } from 'components/layout/wizards';
import { SelectField } from 'components/forms';
import { selectSettings } from 'store/slices/authSlice';
import { formatSchedule } from 'utils/date';
import { formatSeasonalRange, hasSeasonalRange } from 'utils/reminders';
import { TIMEZONE_OPTIONS } from 'constants/date';
import {
  REMINDER_TYPES_LABELS,
  REMINDER_FREQUENCIES_LABELS,
  REMINDER_TYPES,
  REMINDER_RANGE_SUPPORTED_FREQUENCIES,
} from 'constants/reminders';

export default function ReviewStep() {
  const { nextStep, data } = useWizard();
  const user = useSelector((state) => state.auth.user);
  const settings = useSelector(selectSettings);
  const isSubmitting = Boolean(data.isSubmitting);
  const activeWindowValue = data.type === REMINDER_TYPES.REPEATING
    && REMINDER_RANGE_SUPPORTED_FREQUENCIES.includes(data.frequency)
    ? (hasSeasonalRange(data.schedule)
      ? formatSeasonalRange(
        data.schedule?.rangeStartMonth,
        data.schedule?.rangeStartDay,
        data.schedule?.rangeEndMonth,
        data.schedule?.rangeEndDay,
        settings?.dateFormat,
      )
      : 'Year-round')
    : null;

  const { control, handleSubmit } = useForm({
    defaultValues: {
      timezone: data.schedule?.timezone || user?.settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  });

  const onSubmit = (formData) => {
    nextStep({
      schedule: {
        ...data.schedule,
        timezone: formData.timezone
      }
    });
  };

  const renderDetailRow = (label, value) => {
    if (!value) return null;
    return (
      <div className="flex justify-between py-3 border-b border-white/5 last:border-0">
        <span className="text-gray-400">{label}</span>
        <span className="text-white font-medium text-right">{value}</span>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <WizardStepLayout
        title="Review Reminder"
        description="Review details and set timezone before creating."
        primaryAction={{
          label: 'Create Reminder',
          type: 'submit',
          disabled: isSubmitting,
          saving: isSubmitting,
        }}
      >
        <div className="space-y-8 max-w-xl mx-auto">
          <div className="bg-gray-800/50 rounded-xl p-6 space-y-1">
            {renderDetailRow('Type', REMINDER_TYPES_LABELS[data.type])}
            {data.frequency && renderDetailRow('Frequency', REMINDER_FREQUENCIES_LABELS[data.frequency])}
            {renderDetailRow('Schedule', formatSchedule(data, settings))}
            {renderDetailRow('Active window', activeWindowValue)}
            {renderDetailRow('Subject', data.subject)}
            {renderDetailRow('Message', data.message)}
          </div>

          <div className="max-w-md mx-auto">
            <Controller
              name="timezone"
              control={control}
              render={({ field }) => (
                <SelectField
                  label="Timezone"
                  options={TIMEZONE_OPTIONS}
                  disabled={isSubmitting}
                  {...field}
                />
              )}
            />
          </div>
        </div>
      </WizardStepLayout>
    </form>
  );
}
